import React, { useCallback, useRef } from 'react';
import { useStyletron } from 'baseui';
import { useAtomValue, useSetAtom } from 'jotai';
import { RELATIONSHIPS_EVENT_NAMES } from '../../../../../analytics/event-name';
import useAnalytics from '../../../../../analytics/useAnalytics';
import { useDrawer } from '../../../../../common/multi-level-drawer';
import type { RelationshipT } from '../../../../../modernization-common/relationships';
import { RELATIONSHIP_CREATOR_CONSTANTS } from '../../../../../modernization-common/relationships';
import { getRelationshipBasePayload } from '../../../../../modernization-common/relationships/analytics/utils';
import { type DrawerProps, drawerAtom } from '../../../../../modernization-common/side-panel/atoms/drawer.atom';
import { type DrawerModeT, DRAWER_MODE } from '../../../../../modernization-common/side-panel/const';
import { EDITOR_MODE_TYPE } from '../../../../labelling/types';
import { isVideoTaskAtom } from '../../../atoms/annotation-atoms';
import { dispatchAtom } from '../../../atoms/label-store-dispatch/dispatch';
import { EVENTS, UNDOABLE_EVENTS } from '../../../atoms/label-store-dispatch/events';
import { relationshipEditorCleanupAtom, relationshipEditorInitAtom, relationshipStoreWithLabelDataAtom } from '../../../atoms/relationship-atoms';
import { RelationshipEditor } from '../../relationship-component/relationship-editor';
type RelationshipHandlersOptionsT = {
  drawerMode?: DrawerModeT;
};
export const useRelationshipHandlers = ({
  drawerMode = DRAWER_MODE.SIDE_DRAWER
}: RelationshipHandlersOptionsT = {}) => {
  const dispatch = useSetAtom(dispatchAtom);
  const setDrawer = useSetAtom(drawerAtom);
  const currentDrawer = useAtomValue(drawerAtom);
  const {
    open: openStackedDrawer
  } = useDrawer();
  const savedDrawerRef = useRef<DrawerProps>(currentDrawer);
  const initEditor = useSetAtom(relationshipEditorInitAtom);
  const cleanupEditor = useSetAtom(relationshipEditorCleanupAtom);
  const relationshipStore = useAtomValue(relationshipStoreWithLabelDataAtom);
  const isVideoTask = useAtomValue(isVideoTaskAtom);
  const {
    sendEvent
  } = useAnalytics();
  const [, theme] = useStyletron();
  const sendDeleteRelationshipEvent = useCallback((relationship: RelationshipT) => {
    sendEvent({
      action: RELATIONSHIPS_EVENT_NAMES.DELETE_RELATIONSHIP,
      payload: {
        ...getRelationshipBasePayload(relationship),
        deletionType: ('full' as const)
      }
    });
  }, [sendEvent]);
  const closeRelationshipEditor = useCallback(() => {
    cleanupEditor();
    dispatch({
      type: EVENTS.TOGGLE_ANNOTATION_RELATIONSHIP_VIEW,
      editorMode: EDITOR_MODE_TYPE.LABELING
    });
    switch (drawerMode) {
      case DRAWER_MODE.SIDE_DRAWER:
        setDrawer(savedDrawerRef.current);
        break;
      case DRAWER_MODE.STACKED_DRAWER:
        break;
    }
  }, [cleanupEditor, dispatch, drawerMode, setDrawer]);
  const openRelationshipEditor = useCallback((params: {
    relationshipId?: string;
    isSourceEditable?: boolean;
  }) => {
    savedDrawerRef.current = currentDrawer;
    const relationship = params.relationshipId ? relationshipStore[params.relationshipId] : undefined;
    initEditor({
      relationship,
      isSourceEditable: params.isSourceEditable
    });
    dispatch({
      type: EVENTS.TOGGLE_ANNOTATION_RELATIONSHIP_VIEW,
      editorMode: EDITOR_MODE_TYPE.RELATIONSHIP
    });
    switch (drawerMode) {
      case DRAWER_MODE.STACKED_DRAWER:
        openStackedDrawer({
          id: params.relationshipId ? `edit-relationship-${params.relationshipId}` : 'create-relationship',
          title: RELATIONSHIP_CREATOR_CONSTANTS.TITLE,
          description: RELATIONSHIP_CREATOR_CONSTANTS.SUBTITLE,
          content: ({
            onClose
          }) => <RelationshipEditor onClose={onClose} isVideoTask={isVideoTask} />,
          onClose: closeRelationshipEditor,
          styleOverrides: {
            header: {
              borderBottom: 'none',
              padding: theme.sizing.scale600,
              paddingBottom: theme.sizing.scale700
            },
            content: {
              padding: 0
            }
          }
        });
        break;
      case DRAWER_MODE.SIDE_DRAWER:
        setDrawer({
          title: () => RELATIONSHIP_CREATOR_CONSTANTS.TITLE,
          description: RELATIONSHIP_CREATOR_CONSTANTS.SUBTITLE,
          component: ({
            onClose
          }) => <RelationshipEditor onClose={onClose} isVideoTask={isVideoTask} />,
          onClose: closeRelationshipEditor,
          isOpen: true
        });
        break;
    }
  }, [dispatch, theme, setDrawer, currentDrawer, closeRelationshipEditor, initEditor, relationshipStore, drawerMode, openStackedDrawer, isVideoTask]);
  const createRelationship = useCallback(() => {
    openRelationshipEditor({
      isSourceEditable: false
    });
  }, [openRelationshipEditor]);
  const createRelationshipWithEditableSource = useCallback(() => {
    openRelationshipEditor({
      isSourceEditable: true
    });
  }, [openRelationshipEditor]);
  const editRelationship = useCallback((relationshipId: string) => {
    openRelationshipEditor({
      relationshipId
    });
  }, [openRelationshipEditor]);
  const deleteRelationship = useCallback((relationshipId: string) => {
    const relationship = relationshipStore[relationshipId];
    if (relationship) {
      sendDeleteRelationshipEvent(relationship);
    }
    dispatch({
      type: UNDOABLE_EVENTS.DELETE_RELATIONSHIP,
      relationshipIds: [relationshipId]
    });
  }, [dispatch, sendDeleteRelationshipEvent, relationshipStore]);
  return {
    createRelationship,
    createRelationshipWithEditableSource,
    editRelationship,
    closeRelationshipEditor,
    deleteRelationship
  };
};